import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import i18n from '@/i18n'
import { useAuthStore } from '@/app/store/auth'
import { authApi } from '@/lib/api/clients/auth'
import { handleApiError } from '@/lib/api/axios'
import { useToast } from '@/hooks/use-toast'
import type { LoginRequest, MfaChallengeRequest } from '@/lib/api/types'

// Query keys
export const authKeys = {
    me: ['auth', 'me'] as const,
}

// Get current user
export function useMe() {
    const { setUser, setLoading, mfaRequired } = useAuthStore()

    return useQuery({
        queryKey: authKeys.me,
        queryFn: async () => {
            setLoading(true)
            try {
                const response = await authApi.me()
                const user = response.data.user
                setUser(user)
                
                // Apply user's language preference to i18next if available
                if (user?.language && i18n.language !== user.language) {
                    console.log('🌐 Applying user language preference:', user.language)
                    i18n.changeLanguage(user.language)
                    // Also save to localStorage for persistence
                    localStorage.setItem('i18nextLng', user.language)
                }
                
                return user
            } finally {
                setLoading(false)
            }
        },
        enabled: !mfaRequired, // Don't fetch user data when MFA is required
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

// Login mutation
export function useLogin() {
    const navigate = useNavigate()
    const { setUser, setMfaRequired, setTokens, setSessionId } = useAuthStore()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async (data: LoginRequest) => {
            const response = await authApi.login(data)
            return response.data
        },
        onSuccess: (data) => {
            // Only log in development mode
            if (import.meta.env.DEV) {
                console.log('🔐 Login successful, user ID:', data.user?.id)
            }

            // If MFA not required and tokens are present, set tokens FIRST, then user
            if (!data.mfaRequired && data.accessToken && data.refreshToken) {
                setTokens(data.accessToken, data.refreshToken, 15 * 60)
                setUser(data.user)
            } else {
                // MFA flow or missing tokens: set user (not authenticated) and route to MFA
                setUser(data.user)
            }

            // Set session ID if available
            if (data.sessionId) {
                setSessionId(data.sessionId)
            }

            // DEBUG: Check auth state after all operations
            const finalAuthState = useAuthStore.getState()
            // Only log in development mode
            if (import.meta.env.DEV) {
                console.log('🔐 Auth state ready:', {
                    isAuthenticated: finalAuthState.isAuthenticated,
                    userId: finalAuthState.user?.id
                })
            }

            if (data.mfaRequired) {
                setMfaRequired(true, data.methods, data.needsSetup || false)
                navigate('/mfa')
            } else {
                navigate('/')
            }
        },
        onError: (error) => {
            // Clear any existing authentication state on login failure
            const { logout } = useAuthStore.getState()
            logout()
            toast({
                title: 'Login Failed',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// Logout mutation
export function useLogout() {
    const navigate = useNavigate()
    const { logout } = useAuthStore()
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async () => {
            await authApi.logout()
        },
        onSuccess: () => {
            logout()
            queryClient.clear()
            navigate('/login')
            toast({
                title: 'Logged out',
                description: 'You have been successfully logged out.',
            })
        },
        onError: (error) => {
            // Even if logout fails on server, clear local state
            logout()
            queryClient.clear()
            navigate('/login')
            toast({
                title: 'Logged out',
                description: 'You have been logged out locally.',
            })
        },
    })
}

// Force logout - bypasses server call entirely
export function useForceLogout() {
    const { forceLogout } = useAuthStore()
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return () => {
        queryClient.clear()
        forceLogout()
        toast({
            title: 'Logged out',
            description: 'You have been logged out.',
        })
    }
}

// MFA challenge mutation
export function useMfaChallenge() {
    const navigate = useNavigate()
    const { setUser, clearMfa, setTokens, setSessionId, user } = useAuthStore()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async (data: MfaChallengeRequest) => {
            // Include email from user store for TOTP challenge
            const requestData = {
                ...data,
                email: data.email || user?.email || 'admin@backupscheduler.com'
            }
            const response = await authApi.mfaChallenge(requestData)
            return response.data
        },
        onSuccess: (data) => {
            if (import.meta.env.DEV) {
                console.log('🔐 TOTP verification successful, user ID:', data.user?.id)
            }
            
            setUser(data.user)

            // Check if WebAuthn setup is still needed after TOTP verification
            if (data.webauthnNeedsSetup && data.mfaRequired) {
                if (import.meta.env.DEV) {
                    console.log('🔐 TOTP verified, redirecting to WebAuthn setup')
                }
                // Keep MFA required and set the flag for WebAuthn setup
                setMfaRequired(true, data.methods || ['webauthn'], true)
                navigate('/mfa')
                toast({
                    title: 'TOTP Verified',
                    description: 'Please set up your passkey to complete login.',
                })
                return
            }

            // Set tokens if available
            if (data.accessToken && data.refreshToken) {
                setTokens(data.accessToken, data.refreshToken, 15 * 60) // 15 minutes
                if (import.meta.env.DEV) {
                    console.log('🔐 TOTP login complete, tokens set')
                }
            }

            // Set session ID if available
            if (data.sessionId) {
                setSessionId(data.sessionId)
            }

            clearMfa()
            navigate('/')
            toast({
                title: 'Login Successful',
                description: 'Welcome back!',
            })
        },
        onError: (error) => {
            // Clear authentication state on MFA failure
            const { logout } = useAuthStore.getState()
            logout()
            toast({
                title: 'MFA Failed',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// MFA setup mutation
export function useMfaSetup() {
    const { toast } = useToast()

    return useMutation({
        mutationFn: async () => {
            const response = await authApi.mfaSetup()
            return response.data
        },
        onError: (error) => {
            toast({
                title: 'MFA Setup Failed',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// MFA confirm mutation
export function useMfaConfirm() {
    const { toast } = useToast()

    return useMutation({
        mutationFn: async (data: MfaChallengeRequest) => {
            await authApi.mfaConfirm(data)
        },
        onSuccess: () => {
            toast({
                title: 'MFA Enabled',
                description: 'Two-factor authentication has been successfully enabled.',
            })
        },
        onError: (error) => {
            toast({
                title: 'MFA Setup Failed',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// TOTP setup mutation
export function useTotpSetup() {
    const { toast } = useToast()

    return useMutation({
        mutationFn: async () => {
            const response = await authApi.totpSetup()
            return response.data
        },
        onError: (error) => {
            toast({
                title: 'TOTP Setup Failed',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// Initial TOTP setup mutation (no authentication required)
export function useTotpInitialSetup() {
    const { toast } = useToast()

    return useMutation({
        mutationFn: async (email: string) => {
            const response = await authApi.totpInitialSetup(email)
            return response.data
        },
        onError: (error) => {
            toast({
                title: 'TOTP Setup Failed',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// TOTP verify mutation
export function useTotpVerify() {
    const navigate = useNavigate()
    const { setUser, clearMfa, setTokens, setSessionId, user } = useAuthStore()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async (data: { token: string }) => {
            // Include email for initial setup verification
            const requestData = { ...data, email: user?.email }
            const response = await authApi.totpVerify(requestData)
            return response.data
        },
        onSuccess: (data) => {
            // Clear MFA state first
            clearMfa()

            // Set tokens if available
            if (data.accessToken && data.refreshToken) {
                setTokens(data.accessToken, data.refreshToken, 15 * 60) // 15 minutes
            }

            // Set session ID if available
            if (data.sessionId) {
                setSessionId(data.sessionId)
            }

            // Set user last (this will set isAuthenticated: true)
            setUser(data.user)

            navigate('/')
            toast({
                title: 'TOTP Setup Complete',
                description: 'Two-factor authentication has been successfully enabled.',
            })
        },
        onError: (error) => {
            toast({
                title: 'TOTP Verification Failed',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}
